import React, { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { uniqBy, concat, cloneDeep } from 'lodash';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  customStylesForOCP,
  bomSelection,
  customSectionPlacementArry,
  prouctCustmSectionAttributeList,
  productCpAttributeList,
  productCpoutComeList,
  prouctBomOutComeList,
  prouctCustmSectionOutComeList,
  PCPSublocationList,
  poProductoutComeList,
  filterCustomAttributesDropdownAndTextType,
} from '../../../../../Components/helper';
import Loader from '../../../../../Components/Utility/Loader/Loader';

export default function TableSourceComponent({
  columnIndex,
  handleChange,
  relation,
  locationOptions,
  customOptions,
  controlOptions,
  manualCustomColumns,
  hasError,
}) {
  const {
    SystemMastersDetails: { customAttributeList, placement_master, subBrandAccess },
    customSectionData: { bomTypes, sampleTrackingDates },
    CustomModuleReducer: { customModulePages },
  } = useSelector((reducer) => reducer);
  const { t } = useTranslation('common');
  const [isTableControlSelected, setIsTableControlSelected] = useState(false);
  const [tableColumns, setTableColumns] = useState([]);
  const [dynamicBomSelection, setDynamicBomSelection] = useState([]);
  const careUserDocumentInformation = 'Care/User_Document_Information';

  // Create dynamic BOM selection with all BOM types plus bomSelection options
  useEffect(() => {
    if (bomTypes && bomTypes.length > 0) {
      const bomTypeOptions = bomTypes.map((bomType) => ({
        label: bomType.name,
        name: bomType._id,
      }));
      setDynamicBomSelection([...bomTypeOptions, ...bomSelection]);
    } else {
      // Fallback to static options when no BOM types available
      setDynamicBomSelection([...bomSelection]);
    }
  }, [bomTypes]);

  useEffect(() => {
    if (relation?.control?.key === 'Table') {
      setIsTableControlSelected(true);
      setTableColumns(relation?.control?.tableColumns?.filter((eleCol) => eleCol?.key === 'Status'));
    } else {
      setIsTableControlSelected(false);
      setTableColumns([]);
    }
  }, [relation?.control]);

  const placementLabel = () => {
    if (relation?.location?.name === 'Bill_of_Materials' && relation?.locationType?.name !== 'all_item') {
      return t('CUSTOM_LIBRARY.SOURCE_TYPE.PLACEMENT');
    }
    return t('CUSTOM_LIBRARY.SOURCE_TYPE.POSITION');
  };

  const setOptionsForAttribute = () => {
    if (
      [
        'Product_Core_Information',
        'Product_Information',
        'Supplier_Information',
        'Specifications',
        'Samples',
        'Costing_Information',
        'Completion_Indicator',
      ].includes(relation?.location?.name)
    ) {
      return [...productCpAttributeList.filter((attributeElement) => ['valueType'].includes(attributeElement?.name))];
    }
    if (relation?.location?.name === 'Purchase_Order') {
      return [...productCpAttributeList.filter((attributeElement) => ['all_item', 'valueType'].includes(attributeElement?.name))];
    }
    if (relation?.location?.name === 'Bill_of_Materials') {
      return [...productCpAttributeList.filter((attributeElement) => attributeElement?.name !== 'valueType')];
    }
    if (['Design_Details'].includes(relation?.location?.name)) {
      return [...productCpAttributeList.filter((attributeElement) => ['first_item', 'last_item'].includes(attributeElement?.name))];
    }
    if (['Testing', careUserDocumentInformation].includes(relation?.location?.name)) {
      return [...productCpAttributeList.filter((attributeElement) => !['valueType'].includes(attributeElement?.name))];
    }
    if (relation?.location?._id === 12) {
      return [...productCpAttributeList.filter((attributeElement) => attributeElement?.name !== 'all_item')];
    }
    return productCpAttributeList;
  };

  const setPlacementForLocation = () => {
    if (['Bill_of_Materials'].includes(relation?.location?.name)) {
      const selectedBomTypeId = relation?.locationType?.name;
      if (!selectedBomTypeId || selectedBomTypeId === 'all_item') {
        return placement_master?.map((val) => ({ name: val?._id, label: val?.name })) ?? [];
      }
      const filteredPlacementMasters = placement_master?.filter((val) => Array.isArray(val?.bomType) && val?.bomType?.includes(selectedBomTypeId)) ?? [];
      return filteredPlacementMasters.map((val) => ({ name: val?._id, label: val?.name }));
    }
    return customSectionPlacementArry;
  };

  const hideShowAttribute = () => {
    return (
      [
        'Design_Details',
        'Specifications',
        'Testing',
        careUserDocumentInformation,
        'Costing_Information',
        'Purchase_Order',
        'Product_Core_Information',
        'Product_Information',
        'Supplier_Information',
        'Completion_Indicator',
      ].includes(relation?.location?.name) ||
      relation?.location?._id === 12 ||
      (relation?.location?.name === 'Bill_of_Materials' && relation?.locationPlacement) ||
      (relation?.location?.name === 'Bill_of_Materials' && relation?.locationType?.name === 'all_item') ||
      (relation?.location?.name === 'Samples' && relation?.locationPlacement?.name)
    );
  };

  const hideShowPlacement = () => {
    return (
      (relation?.location?.name === 'Bill_of_Materials' && relation?.locationType && relation?.locationType?.name !== 'all_item') ||
      (relation?.location?.name === 'Samples' && relation?.locationType?.name)
    );
  };

  const hideShowSubLocation = () => {
    return (
      ['Product_Core_Information', 'Product_Information', 'Supplier_Information', 'Specifications', 'Costing_Information'].includes(relation?.location?.name) ||
      (relation?.location?._id === 12 && relation?.output) ||
      (relation?.location?.name === 'Bill_of_Materials' && relation?.locationType?.name !== 'all_item' && relation?.output?.name === 'valueType') ||
      (relation?.location?.name === 'Samples' && relation?.attribute?.name) ||
      (relation?.location?.name === 'Purchase_Order' && relation?.attribute?.name === 'valueType') ||
      (relation?.location?.name === 'Completion_Indicator' && relation?.attribute?.name) ||
      relation?.location?._id === 7
    );
  };

  const hideShowOutput = () => {
    if ((relation?.location?.name === 'Bill_of_Materials' || relation?.location?._id === 12) && relation?.attribute?.name === 'valueType') {
      return false;
    }
    return (
      ((['Design_Details', 'Testing', 'Bill_of_Materials', 'Care/User_Document_Information', 'Custom_Section'].includes(relation?.location?.name) ||
        relation?.location?._id === 12) &&
        relation?.attribute?.name) ||
      (relation?.location?.name === 'Purchase_Order' && relation?.attribute?.name === 'all_item')
    );
  };

  const testingOutputList = () => {
    return productCpoutComeList.filter((outputItem) =>
      ['first_item', 'last_item'].includes(relation?.attribute?.name)
        ? ['date_added'].includes(outputItem?.name)
        : outputItem?.name === 'percentage_of_approval',
    );
  };

  const setOptionsForOutput = () => {
    if (['Testing', careUserDocumentInformation].includes(relation?.location?.name)) {
      return [...testingOutputList()];
    }
    if (relation?.location?.name === 'Design_Details') {
      return [...productCpoutComeList.filter((outputItem) => ['date_added'].includes(outputItem?.name)), { label: 'Status Date', name: 'status_date' }];
    }
    if (relation?.location?._id === 12) {
      return [...productCpoutComeList.filter((outputItem) => ['date_added'].includes(outputItem?.name))];
    }
    if (relation?.location?.name === 'Purchase_Order') {
      return [...poProductoutComeList.filter((outputItem) => outputItem?.name === 'completion_percentage')];
    }
    if (relation?.location?.name === 'Bill_of_Materials') {
      if (relation?.locationType?.name !== 'all_item' && ['first_item', 'last_item'].includes(relation?.attribute?.name)) {
        return [...productCpoutComeList.filter((outputItem) => ['date_added'].includes(outputItem?.name))];
      }
      return ['first_item', 'last_item'].includes(relation?.attribute?.name)
        ? [...productCpoutComeList.filter((outputItem) => ['date_added'].includes(outputItem?.name))]
        : [...productCpoutComeList.filter((outputItem) => outputItem?.name === 'percentage_of_approval')];
    }
    return relation?.location?._id === 12 ? prouctCustmSectionOutComeList : productCpoutComeList;
  };

  const setOptionsForBomOutput = () => {
    if (
      relation?.location?.name === 'Bill_of_Materials' &&
      relation?.locationType?.name !== 'all_item' &&
      ['first_item', 'last_item'].includes(relation?.attribute?.name)
    ) {
      return prouctBomOutComeList.filter((ele) => ele.name !== 'percentage_of_approval');
    }
    if (
      relation?.location?.name === 'Bill_of_Materials' &&
      relation?.locationType?.name === 'all_item' &&
      ['first_item', 'last_item'].includes(relation?.attribute?.name)
    ) {
      return prouctBomOutComeList.filter((ele) => ele.name === 'date_added');
    }
    if (relation?.location?.name === 'Bill_of_Materials' && relation?.attribute?.name === 'all_item') {
      return prouctBomOutComeList.filter((ele) => ele.name === 'percentage_of_approval');
    }
    return prouctCustmSectionOutComeList;
  };

  const pageSubLocations = () => {
    return customModulePages?.pages?.map((element) => ({
      label: element?.pageTitle,
      name: element?._id,
      type: 'page',
    }));
  };

  const setSubLocationArr = () => {
    const locationId = relation?.location?._id;
    if (locationId === 12) {
      return manualCustomColumns;
    }
    let subLocationArr = cloneDeep(PCPSublocationList?.find((val) => val?.locationId === locationId) || {});
    if (!subLocationArr?.subLocations) {
      const findLocation = locationOptions.find((loc) => loc[columnIndex]?.name === relation?.location?.name);
      subLocationArr = cloneDeep(PCPSublocationList?.find((val) => val?.locationId === findLocation?._id) || {});
    }
    let manipulatedDynamicColumns = [];
    const mcColumn = filterCustomAttributesDropdownAndTextType(customAttributeList);
    manipulatedDynamicColumns = [];
    mcColumn?.forEach((dyCol) => {
      if (dyCol.isActive) {
        manipulatedDynamicColumns.push({
          label: dyCol?.keyName,
          name: dyCol?._id,
          type: 'string',
        });
      }
    });
    const sampleTrackingArr = sampleTrackingDates?.map((dyCol) => {
      return {
        label: dyCol?.name,
        name: dyCol?._id,
        type: 'date',
      };
    });
    if (relation?.location?.name === 'Product_Information') {
      subLocationArr.subLocations = concat(subLocationArr?.subLocations, manipulatedDynamicColumns);
    }

    if (relation?.location?.name === 'Specifications') {
      subLocationArr.subLocations = concat(subLocationArr?.subLocations);
    }
    if (relation?.location?.name === 'Samples') {
      subLocationArr.subLocations = concat(subLocationArr?.subLocations, sampleTrackingArr);
    }

    if (relation?.location?._id === 11 && Array.isArray(subLocationArr?.subLocations) && subBrandAccess) {
      subLocationArr.subLocations = concat(subLocationArr.subLocations, [{ label: 'Sub Brand', name: 'subBrand', type: 'string' }]);
    }

    return subLocationArr.subLocations;
  };

  const hideShowPage = () => {
    return relation?.location?._id === 7 && relation?.subLocation;
  };
  const hideShowControl = () => {
    return relation?.location?._id === 7 && relation?.subLocation && relation?.pages;
  };

  return relation?.loading ? (
    <Loader />
  ) : (
    <div key={`source_Table_Column_${columnIndex}`}>
      <Row className="px-3">
        <Col lg={12} className="mb-3 mt-3 p-0">
          <p className="asterisk-req f-14 mb-0">
            {t('CUSTOM_LIBRARY.SOURCE_TYPE.LOCATION')} <span>*</span>
          </p>
          <Select
            name="location"
            options={uniqBy(locationOptions, 'name')} // locationList
            getOptionLabel={(option) => `${option.label}`}
            getOptionValue={(option) => `${option.name}`}
            maxMenuHeight={140}
            value={relation?.location}
            menuPosition="fixed"
            styles={customStylesForOCP}
            onChange={(e) =>
              handleChange('location', e, ['subLocation', 'attribute', 'output', 'locationType', 'locationPlacement', 'pages', 'control'], columnIndex)
            }
            placeholder={t('CUSTOM_MODULES.LIBRARY.TABLE.TABLE_SOURCE_COMPONENT.SELECT_LOCATION')}
          />
          {hasError && !relation?.location && <span className="error-message">{t('CUSTOM_LIBRARY.SOURCE_TYPE.LOCATION_MISSING')}</span>}
        </Col>
        {['Bill_of_Materials', 'Samples'].includes(relation?.location?.name) && (
          <Col lg="12" className=" mb-3 p-0">
            <p className="asterisk-req f-14 mb-0">
              {t('CUSTOM_LIBRARY.SOURCE_TYPE.LOCATION_TYPE')} <span>*</span>
            </p>
            <Select
              name="locationType"
              isLoading={relation?.loading}
              options={relation?.location?.name === 'Samples' ? relation?.sampleType : dynamicBomSelection || []}
              getOptionLabel={(option) => `${option.label}`}
              getOptionValue={(option) => `${option.name}`}
              value={relation?.locationType}
              styles={customStylesForOCP}
              maxMenuHeight={120}
              placeholder={t('CUSTOM_MODULES.LIBRARY.TABLE.TABLE_SOURCE_COMPONENT.SELECT_TYPE')}
              menuPosition="fixed"
              onChange={(selected) =>
                handleChange('locationType', selected, ['subLocation', 'attribute', 'output', 'locationPlacement', 'pages', 'control'], columnIndex)
              }
            />
            {hasError && !relation?.locationType && (
              <span className="error-message">{`${
                relation?.location?.name === 'Samples' ? t('CUSTOM_LIBRARY.SOURCE_TYPE.SAMPLE_TYPE_MISSING') : t('CUSTOM_LIBRARY.SOURCE_TYPE.BOM_TYPE_MISSING')
              }`}</span>
            )}
          </Col>
        )}
        {hideShowPlacement() && (
          <Col lg="12" className=" mb-3 p-0">
            <p className="asterisk-req f-14 mb-0">
              {placementLabel()} <span>*</span>
            </p>
            <Select
              name="locationPlacement"
              options={setPlacementForLocation() || []}
              getOptionLabel={(option) => `${option.label}`}
              getOptionValue={(option) => `${option.name}`}
              value={relation?.locationPlacement}
              styles={customStylesForOCP}
              maxMenuHeight={120}
              menuPosition="fixed"
              placeholder={placementLabel()}
              isLoading={relation?.loading}
              onChange={(selected) => handleChange('locationPlacement', selected, ['subLocation', 'attribute', 'output', 'pages', 'control'], columnIndex)}
            />
            {hasError && !relation?.locationPlacement && <span className="error-message">{`${placementLabel()} is required.`}</span>}
          </Col>
        )}
        {hideShowAttribute() && (
          <Col lg={12} className="mb-3 p-0">
            <p className="asterisk-req f-14 mb-0">
              {t('CUSTOM_LIBRARY.SOURCE_TYPE.ATTRIBUTE')} <span>*</span>
            </p>
            <Select
              name="attribute"
              options={relation?.location?._id === 12 ? prouctCustmSectionAttributeList : setOptionsForAttribute()}
              getOptionLabel={(option) => `${option.label}`}
              getOptionValue={(option) => `${option.name}`}
              value={relation?.attribute}
              maxMenuHeight={140}
              menuPlacement="bottom"
              menuPosition="fixed"
              menuShouldBlockScroll
              menuShouldScrollIntoView
              styles={customStylesForOCP}
              isLoading={relation?.loading}
              onChange={(selected) => handleChange('attribute', selected, ['subLocation', 'output', 'pages', 'control'], columnIndex)}
              placeholder={t('CUSTOM_MODULES.LIBRARY.TABLE.TABLE_SOURCE_COMPONENT.SELECT_ATTRIBUTE')}
            />
            {hasError && !relation?.attribute && <span className="error-message">{t('CUSTOM_LIBRARY.SOURCE_TYPE.ATTRIBUTE_MISSING')}</span>}
          </Col>
        )}
        {hideShowOutput() && (
          <Col lg={12} className="mb-3 p-0">
            <p className="asterisk-req f-14 mb-0">
              {t('CUSTOM_LIBRARY.SOURCE_TYPE.OUTPUT')} <span>*</span>
            </p>
            <Select
              name="Output"
              options={relation?.location?.name === 'Bill_of_Materials' ? setOptionsForBomOutput() : setOptionsForOutput()}
              getOptionLabel={(option) => `${option.label}`}
              getOptionValue={(option) => `${option.name}`}
              value={relation?.output}
              maxMenuHeight={140}
              styles={customStylesForOCP}
              menuPlacement="bottom"
              menuPosition="fixed"
              menuShouldBlockScroll
              menuShouldScrollIntoView
              placeholder={t('CUSTOM_MODULES.LIBRARY.TABLE.TABLE_SOURCE_COMPONENT.SELECT_OUTCOME')}
              onChange={(selected) => handleChange('output', selected, ['subLocation', 'pages', 'control'], columnIndex)}
              isLoading={relation?.loading}
            />
            {hasError && !relation?.output && <span className="error-message">{t('CUSTOM_LIBRARY.SOURCE_TYPE.OUTPUT_MISSING')}</span>}
          </Col>
        )}
        {hideShowSubLocation() && (
          <Col lg="12" className="mb-3 p-0">
            <p className="asterisk-req f-14 mb-0">
              {t('CUSTOM_LIBRARY.SOURCE_TYPE.SUB_LOCATION')} <span>*</span>
            </p>
            <Select
              name="SubLocation"
              options={(relation?.location?._id === 7 ? customOptions : setSubLocationArr()) || []}
              getOptionLabel={(option) => `${option.label}`}
              getOptionValue={(option) => `${option.name}`}
              value={relation?.subLocation}
              styles={customStylesForOCP}
              maxMenuHeight={120}
              menuPosition="fixed"
              placeholder={t('CUSTOM_MODULES.LIBRARY.TABLE.TABLE_SOURCE_COMPONENT.SELECT_SUB_LOCATION')}
              isLoading={relation?.loading}
              onChange={(selected) => handleChange('subLocation', selected, ['pages', 'control'], columnIndex)}
            />
            {hasError && !relation?.subLocation && <span className="error-message">{t('CUSTOM_LIBRARY.SOURCE_TYPE.SUB_LOCATION_MISSING')}</span>}
          </Col>
        )}
        {hideShowPage() && (
          <Col lg="12" className="mb-3 p-0">
            <p className="asterisk-req f-14 mb-0">
              {t('CUSTOM_LIBRARY.SOURCE_TYPE.PAGES')} <span>*</span>
            </p>
            <Select
              name="pages"
              options={pageSubLocations() || []}
              getOptionLabel={(option) => `${option.label}`}
              getOptionValue={(option) => `${option.name}`}
              value={relation?.pages}
              styles={customStylesForOCP}
              maxMenuHeight={120}
              menuPosition="fixed"
              placeholder={t('CUSTOM_MODULES.LIBRARY.TABLE.TABLE_SOURCE_COMPONENT.SELECT_PAGE')}
              isLoading={relation?.loading}
              onChange={(selected) => handleChange('pages', selected, ['control'], columnIndex)}
            />
            {hasError && !relation?.subLocation && <span className="error-message">{t('CUSTOM_LIBRARY.SOURCE_TYPE.PAGES_MISSING')}</span>}
          </Col>
        )}
        {hideShowControl() && (
          <Col lg="12" className="mb-3 p-0">
            <p className="asterisk-req f-14 mb-0">
              {t('CUSTOM_LIBRARY.SOURCE_TYPE.CONTROLS')} <span>*</span>
            </p>
            <Select
              name="controls"
              options={controlOptions || []}
              getOptionLabel={(option) => `${option.label}`}
              getOptionValue={(option) => `${option.name}`}
              value={relation?.control}
              styles={customStylesForOCP}
              maxMenuHeight={120}
              menuPosition="fixed"
              placeholder={t('CUSTOM_MODULES.LIBRARY.TABLE.TABLE_SOURCE_COMPONENT.SELECT_CONTROL')}
              isLoading={relation?.loading}
              onChange={(selected) => handleChange('control', selected, [], columnIndex)}
            />
            {hasError && !relation?.subLocation && <span className="error-message">{t('CUSTOM_LIBRARY.SOURCE_TYPE.CONTROLS_MISSING')}</span>}
          </Col>
        )}
        {isTableControlSelected && (
          <Col lg="12" className="mb-3 p-0">
            <p className="asterisk-req f-14 mb-0">
              {t('CUSTOM_LIBRARY.SOURCE_TYPE.TABLE_COLUMN')} <span>*</span>
            </p>
            <Select
              name="controls"
              options={tableColumns || []}
              getOptionLabel={(option) => `${option.label}`}
              getOptionValue={(option) => `${option.fieldName}_${option?.i}`}
              value={relation?.tableColumn}
              menuPosition="fixed"
              styles={customStylesForOCP}
              maxMenuHeight={120}
              placeholder={t('CUSTOM_MODULES.LIBRARY.TEMPLATE_COLUMN.NEW_CONTROL.SOURCE_COMPONENTS.SELECT_COLUMN')}
              onChange={(selected) => handleChange('tableColumn', selected, [], columnIndex)}
            />
          </Col>
        )}
      </Row>
    </div>
  );
}
